import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiGlobe, FiHeart, FiMapPin, FiStar, FiUsers } from 'react-icons/fi';
import tourist from '../assets/tourist-carrying-luggage.jpg';
import { useNavigate } from 'react-router-dom';
import about from '../assets/mark-olsen-K5j1KgecVC8-unsplash.jpg';

export default function About({ isHomePage = false }) {
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);
  
  const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
  const fadeLeft = { hidden: { opacity: 0, x: -32 }, show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
  const fadeRight = { hidden: { opacity: 0, x: 32 }, show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
  
  const stats = [
    { value: '10K+', label: 'Happy Travelers', icon: <FiUsers className="text-3xl text-primary mb-2" /> },
    { value: '50+', label: 'Destinations', icon: <FiMapPin className="text-3xl text-primary mb-2" /> },
    { value: '98%', label: 'Success Rate', icon: <FiStar className="text-3xl text-primary mb-2" /> },
    { value: '24/7', label: 'Support', icon: <FiHeart className="text-3xl text-primary mb-2" /> },
  ];

  const team = [
    { name: 'Sarah Johnson', role: 'CEO & Founder', image: 'https://randomuser.me/api/portraits/women/45.jpg' },
    { name: 'Michael Chen', role: 'Travel Expert', image: 'https://randomuser.me/api/portraits/men/32.jpg' },
    { name: 'Aisha Al-Mansoori', role: 'Customer Success', image: 'https://randomuser.me/api/portraits/women/63.jpg' },
    { name: 'David Wilson', role: 'Tour Guide', image: 'https://randomuser.me/api/portraits/men/86.jpg' },
  ];

  const navigate = useNavigate();
  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center text-center text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${about})`,
            backgroundAttachment: 'fixed'
          }}
        ></div>
        <div className="relative z-10 max-w-4xl px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              Crafting <span className="text-primary">Unforgettable</span> Journeys
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              We transform travel dreams into reality with personalized experiences, expert guidance, and a passion for exploration.
            </p>
            {/* <button className="bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105">
              Explore Our Tours
            </button> */}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-8 text-center"
          >
            {stats.map((stat, index) => (
              <div key={index} className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                {stat.icon}
                <h3 className="text-3xl font-bold text-gray-800 mb-1">{stat.value}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Our Story */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <motion.div 
              variants={fadeLeft}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="md:w-1/2"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={tourist} 
                  alt="Our Story" 
                  className="w-full h-auto rounded-2xl transform hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg w-3/4">
                  <div className="flex items-center">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full mr-4">
                      <FiAward className="text-2xl text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">Best Travel Agency 2023</h4>
                      <p className="text-sm text-gray-500">Awarded by Travel Excellence</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div 
              variants={fadeRight}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="md:w-1/2"
            >
              <span className="inline-block text-primary font-semibold mb-3">OUR STORY</span>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">More Than Just a Travel Agency</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Founded in 2019, One Zone Travels has grown from a small startup to one of the most trusted travel agencies in Oman. 
                Our journey began with a simple mission: to make travel accessible, enjoyable, and truly memorable for everyone.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                We believe that travel has the power to change lives. That's why we go beyond booking flights and hotels – we create 
                experiences that inspire, educate, and connect people with the world's most incredible destinations.
              </p>
              {isHomePage && (
                <button className="flex items-center text-primary font-semibold hover:text-primary-dark transition-colors"
                onClick={() => navigate('/about')}
                >
                  Learn More About Us
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block text-primary font-semibold mb-3">OUR MISSION</span>
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Choose One Zone Travels?</h2>
            <div className="w-24 h-1 bg-primary mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-100px' }}
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-primary"
            >
              <div className="bg-primary bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FiGlobe className="text-2xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-center mb-3">Global Expertise</h3>
              <p className="text-gray-600 text-center">
                Our team of travel experts has extensive knowledge of destinations worldwide, ensuring you get the best travel advice.
              </p>
            </motion.div>
            
            <motion.div 
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-primary"
            >
              <div className="bg-primary bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FiHeart className="text-2xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-center mb-3">Personalized Service</h3>
              <p className="text-gray-600 text-center">
                We take the time to understand your unique travel preferences and craft customized itineraries just for you.
              </p>
            </motion.div>
            
            <motion.div 
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ delay: 0.4 }}
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-primary"
            >
              <div className="bg-primary bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FiStar className="text-2xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-center mb-3">Best Value</h3>
              <p className="text-gray-600 text-center">
                We offer competitive pricing without compromising on quality, ensuring you get the best value for your money.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block text-primary font-semibold mb-3">MEET OUR TEAM</span>
            <h2 className="text-4xl font-bold text-gray-800 mb-6">The People Behind Your Journey</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our team of travel enthusiasts is dedicated to making your travel experience exceptional.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="h-64 overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-primary font-medium">{member.role}</p>
                  <div className="flex justify-center mt-4 space-x-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FiStar key={star} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-primary to-primary-dark text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Your Next Adventure?</h2>
            <p className="text-xl text-white text-opacity-90 mb-8 max-w-2xl mx-auto">
              Let us help you plan the perfect trip tailored to your interests and budget.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105">
                Get a Free Quote
              </button>
              <button className="bg-transparent border-2 border-white hover:bg-white hover:bg-opacity-10 font-semibold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105">
                Contact Our Experts
              </button>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
}